import * as React from 'react';
import { observer } from 'mobx-react';

import { PageCreateAccountViewModel } from './page-create-account-view-model';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { BackButton } from '../../components/back-button/back-button';
import { SubmitButton } from '../../components/submit-button/submit-button';
import { TextboxField } from '../../components/form-fields';
import { FormControl } from '../../components/form-control/form-control';
import { Form } from '../../primitives/form';
import { ErrorMessage } from '../../components/error-message/error-message';
import { PanelContentTransition, panelContentTransitionDuration } from '../../components/panel-content-transition';
import { FadeTransition } from '../../components/fade-transition';
import { FocusOnMount } from '../../components/focus-on-mount-hoc';
import { getPageClasses } from '../helpers/get-page-classes';
import { PanelHeading } from '../../components/panel-heading';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { PanelFooter } from '../../components/panel-footer';
import { Text } from '../../components/text';

import {pageFooter, pageFooterWithBackButton} from '../../main/webgiving-main.less';
import {classNames} from "../../../Shared/utils/classnames";

export { PageCreateAccountViewModel };

@injectWebGivingProps
@observer
export class PageCreateAccount extends React.Component<{
	vm: PageCreateAccountViewModel,
	transitionState: string,
} & InjectableWebGivingProps> {
	panelBodyNode: HTMLDivElement;

	render() {
		const { vm, transitionState } = this.props;
		const { firstName, lastName, emailAddress, error, isBlocked, pageShouldBeVisible, isLoading, subTitle } = vm;
		const elementsShouldEnter = transitionState === 'entered';

		return (
			<div className={getPageClasses(pageShouldBeVisible, isLoading)} data-pp-at-target={pageShouldBeVisible ? `Create Account page` : null}>
				<Form onSubmit={this.handleSubmit}>
					<PanelHeading vm={vm}>
						<FadeTransition in={elementsShouldEnter}>
							<div>
								<h1 className="panel-title"><Text>PERSONAL DETAILS</Text></h1>
								<p className="panel-subtitle"><Text>{!!subTitle ? subTitle :'Enter your name and email to create an account.'}</Text></p>
							</div>
						</FadeTransition>
					</PanelHeading>
					<div className="panel-body panel-body-form" ref={this.savePanelBodyNode}>
						<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
							<div>
								<ErrorMessage error={error} />
								<div className="row">
									<FormControl label="First name" validationError={vm.firstName.validationError} className="col-sm-6">
										<FocusOnMount delay={panelContentTransitionDuration}>
											<TextboxField formField={firstName} acceptanceTestTargetId="First Name" />
										</FocusOnMount>
									</FormControl>
									<FormControl label="Last name" validationError={vm.lastName.validationError} className="col-sm-6">
										<TextboxField formField={lastName} acceptanceTestTargetId="Last Name" />
									</FormControl>
								</div>
								<FormControl label="Email address" validationError={vm.emailAddress.validationError}>
									<TextboxField formField={emailAddress} acceptanceTestTargetId="Email Address" type="email" />
								</FormControl>
							</div>
						</PanelContentTransition>
					</div>
					<PanelFooter className={classNames(pageFooter, pageFooterWithBackButton)}>
						<SubmitButton acceptanceTestTargetId="Next" isLoading={vm.isLoading} isBlocked={isBlocked}>
							<Text>Next</Text>
						</SubmitButton>
						<BackButton acceptanceTestTargetId="Back" onClick={this.handleToPreviousPage} isLoading={vm.isLoading}>
							<Text>Back</Text>
						</BackButton>
					</PanelFooter>
				</Form>
			</div>
		);
	}

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private handleToPreviousPage = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		raiseAction(new WebGivingUserAction.PreviousPage(), this.props);
	}

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		raiseAction(new WebGivingUserAction.SubmitCurrentPage(), this.props);
	}
}
